import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { ArrowUpLongIcon, Button, Caption1, Header2, InfoBox } from "@activecollab/components";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js/pure";
import { stringifyUrl } from "query-string";
import { useAuth } from "~/components/Hooks/useAuth";
import { showErrorToast } from "~/components/Toast";
import PublicLayout from "~/pages/Layouts/PublicLayout";
import PublicStripeCardForm from "~/pages/PublicInvoices/PublicStripeCardForm";
import { usePublicInvoice } from "~/repository";
import { usePublicPaymentStripe } from "~/repository/usePublicPayments";

const PublicPaymentPage = () => {
  useAuth();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [script, setScript] = useState(null);
  const { data, isFetched } = usePublicInvoice(searchParams.get("number"), searchParams.get("hash"), {
    enabled: !!searchParams.get("number") && !!searchParams.get("hash"),
  });
  const { mutate: creditCardPayment } = usePublicPaymentStripe();

  useEffect(() => {
    if (isFetched && data?.credit_card_token) {
      loadStripe(data?.credit_card_token).then((r) => {
        setScript(r);
      });
    }
  }, [data?.credit_card_token, isFetched]);

  const onCreditCardPayment = useCallback(
    (token, setIsSubmitting) => {
      if (token && data?.invoice.id) {
        creditCardPayment(
          { parent_id: data?.invoice.id, parent_type: "Invoice", token: token },
          {
            onSuccess: (r) => {
              setIsSubmitting(false);
              if (r?.single?.additional_properties?.is_3d_secure) {
                window.location = r?.single?.additional_properties?.redirect_url;
              } else {
                navigate(`/s/payment-completed-card?number=${r?.single?.id}&hash=${r?.single?.hash}`, {
                  state: { first: true },
                });
              }
            },
            onError: (r) => {
              setIsSubmitting(false);
              if (r?.response?.message) {
                showErrorToast(r?.response.message);
              }
            },
          }
        );
      }
    },
    [creditCardPayment, data?.invoice.id, navigate]
  );

  return script ? (
    <PublicLayout
      bodyClassName="tw-flex tw-items-center tw-justify-center"
      paperClassName="tw-w-[400px] tw-p-8 tw-flex tw-flex-col tw-items-center"
    >
      <Header2 className="tw-mb-8">{t("Pay with Card")}</Header2>
      <InfoBox
        type="neutral"
        className="tw-mb-8"
      >
        <div className="tw-flex tw-flex-col tw-items-center">
          <Caption1
            weight="bold"
            color="secondary"
          >
            {t("Invoice")} {data?.invoice?.number}
          </Caption1>
          <Caption1
            weight="bold"
            color="secondary"
          >
            {t("Amount")}: {data?.invoice?.balance_due} {data?.currency?.code}
          </Caption1>
        </div>
      </InfoBox>
      <Elements
        stripe={script}
        options={{ locale: data?.language.locale.substr(0, 2) }}
      >
        <PublicStripeCardForm onSubmit={onCreditCardPayment} />
      </Elements>
      <Link
        to={stringifyUrl({
          url: `/s/invoice`,
          query: { number: data?.invoice?.number, hash: data?.invoice?.hash },
        })}
      >
        <Button
          variant="text colored"
          className="tw-mt-8"
        >
          <ArrowUpLongIcon className="-tw-rotate-90" />
          {t("Back To Invoice")}
        </Button>
      </Link>
    </PublicLayout>
  ) : null;
};

export default PublicPaymentPage;
